import { Injectable, computed, signal } from '@angular/core';
import type { TimelineEntry, TimelineKind, TimelineGap, IEducationExperienceTimeline } from 'shared-resources';
import { sortTimeline, detectGaps, detectOverlaps, blankEntry, entriesOfKind } from './timeline.logic';
import type { ClerkshipEntry, FellowshipEntry } from 'shared-resources';

/**
 * Store for the unified Education & Experience timeline.
 *
 * Holds the typed `TimelineEntry[]` (discriminated union) plus applicant gap
 * explanations, and exposes derived chronology / gaps / overlaps / OARM flags.
 * The shape is DB-ready (`toPersistable()` returns the exact
 * `IEducationExperienceTimeline` slice) but persistence is NOT wired yet — the
 * backend save is handled in a later effort.
 *
 * Entry storage order is irrelevant to correctness; chronology is derived.
 * `commitOrder()` rewrites storage order to chronological at save boundaries so
 * the form reopens tidy.
 */
@Injectable({ providedIn: 'root' })
export class EducationExperienceStore {
	private readonly _entries = signal<TimelineEntry[]>([]);
	private readonly _gaps = signal<TimelineGap[]>([]);

	/** Entries in chronological (display) order. */
	readonly sorted = computed(() => sortTimeline(this._entries()));

	/** Detected gaps, carrying any explanation the applicant already typed. */
	readonly gaps = computed(() => detectGaps(this._entries(), this._gaps()));

	/** Detected overlaps (gently flagged). */
	readonly overlaps = computed(() => detectOverlaps(this._entries()));

	/** All clerkship entries, chronological (typed). */
	readonly clerkships = computed<ClerkshipEntry[]>(() => entriesOfKind(this.sorted(), 'clerkship'));

	/** All fellowship entries, chronological (typed). */
	readonly fellowships = computed<FellowshipEntry[]>(() => entriesOfKind(this.sorted(), 'fellowship'));

	/** Raw entries accessor (for the editable card list; insertion order). */
	readonly entries = this._entries.asReadonly();

	addEntry(kind: TimelineKind): TimelineEntry {
		const entry = blankEntry(kind);
		this._entries.update((list) => [...list, entry]);
		return entry;
	}

	removeEntry(id: string): void {
		this._entries.update((list) => list.filter((e) => e.id !== id));
	}

	/**
	 * Patch an entry by id. Typed as a partial of the union; callers pass
	 * kind-appropriate fields (the card components own that correctness).
	 */
	updateEntry(id: string, patch: Partial<TimelineEntry>): void {
		this._entries.update((list) => list.map((e) => (e.id === id ? ({ ...e, ...patch } as TimelineEntry) : e)));
	}

	/** Replace an entry wholesale (used by card components emitting a full entry). */
	replaceEntry(entry: TimelineEntry): void {
		this._entries.update((list) => list.map((e) => (e.id === entry.id ? entry : e)));
	}

	/** Rewrite storage order to chronological (called at save boundaries). */
	commitOrder(): void {
		this._entries.update((list) => sortTimeline(list));
	}

	/** Save (or update) a gap explanation keyed by its bounding entries. */
	setGapExplanation(afterEntryId: string, beforeEntryId: string, explanation: string): void {
		this._gaps.update((list) => {
			const others = list.filter((g) => !(g.afterEntryId === afterEntryId && g.beforeEntryId === beforeEntryId));
			return [...others, { afterEntryId, beforeEntryId, fromDate: '', toDate: '', months: 0, explanation }];
		});
	}

	/**
	 * The exact persistable slice for `IOasisApplicationBase.educationExperience`.
	 * Ready for the backend wireup session; not called anywhere yet.
	 */
	toPersistable(): IEducationExperienceTimeline {
		return {
			entries: sortTimeline(this._entries()),
			gapExplanations: this.gaps(),
		};
	}

	/** Hydrate from a persisted slice (for the future load path). */
	hydrate(slice: IEducationExperienceTimeline | undefined): void {
		this._entries.set(slice?.entries ?? []);
		this._gaps.set(slice?.gapExplanations ?? []);
	}
}
